import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { saveBlob } from "../../lib/files";
import { formatDateTime } from "../../lib/format";

const WORD = "BORRAR";
const REMOVED = [["documentos", "Documentos"], ["por_clasificar", "Archivos en Por clasificar"], ["propuestas_ia", "Propuestas de la IA"], ["sugerencias", "Sugerencias"], ["clientes", "Clientes sin crédito"]];
const KEPT = [["clientes", "Clientes del tape"], ["creditos", "Créditos"], ["usuarios", "Usuarios"]];

const Counts = ({ title, rows, data, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <div className="text-xs font-bold uppercase tracking-wide text-[#5b667a]">{title}</div>
    {rows.map(([k, label]) => (
      <div key={k} className="flex justify-between rounded-lg bg-[#f5f8fd] px-3 py-1.5 text-sm" data-testid={`${testId}-${k}`}>
        <span>{label}</span><span className="font-mono font-bold">{(data[k] ?? 0).toLocaleString("es-MX")}</span>
      </div>
    ))}
  </div>
);

const Preview = ({ pf }) => (
  <div className="space-y-3" data-testid="reset-preview">
    <Counts title="Se quitarán" rows={REMOVED} data={{ ...pf, clientes: pf.clientes_a_borrar.length }} testId="reset-preview-count" />
    <p className="text-sm text-[#3b475c]">Se quedan {pf.clientes_que_quedan} clientes con {pf.creditos} créditos, pagos, calendarios, usuarios, Portfolio e historiales.</p>
    <details className="rounded-lg border border-[#e8edf5] p-2 text-sm" data-testid="reset-clients-list">
      <summary className="cursor-pointer font-semibold">Clientes sin crédito que se borrarán ({pf.clientes_a_borrar.length})</summary>
      <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto font-mono text-xs">
        {pf.clientes_a_borrar.map((c) => <li key={c.borrower_id}>{c.borrower_id} · {c.name}</li>)}
      </ul>
    </details>
  </div>
);

const Progress = ({ job }) => (
  <div className="space-y-2" data-testid="reset-progress">
    <div className="flex items-center gap-2 text-sm font-bold"><Loader2 className="h-4 w-4 animate-spin text-[#0f5ef7]" />{job.step}{job.total ? ` · ${job.done} de ${job.total}` : ""}</div>
    <p className="text-xs text-[#5b667a]" data-testid="reset-progress-updated">Última actualización {formatDateTime(job.updated_at)} · sigue en el servidor aunque recargues la página</p>
  </div>
);

const Summary = ({ job }) => (
  <div className="space-y-3" data-testid="reset-summary">
    {job.message && <p className="rounded-lg bg-[#fff4e5] px-3 py-2 text-sm font-semibold text-[#b54708]" data-testid="reset-message">{job.message}</p>}
    <div className="grid gap-3 sm:grid-cols-2">
      <Counts title="Quitados" rows={REMOVED} data={job.removed} testId="reset-removed" />
      <Counts title="Se quedan" rows={KEPT} data={job.kept} testId="reset-kept" />
    </div>
  </div>
);

export const ResetDocuments = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [pf, setPf] = useState(null);
  const [job, setJob] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState("");
  const running = job?.status === "en_curso";

  const loadJob = useCallback(() => api.get("/reset/jobs/current").then((r) => setJob(r.data)).catch(() => {}), []);
  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => {
    if (!open || running) return;
    setPf(null);
    setWord("");
    api.get("/reset/preview").then((r) => setPf(r.data)).catch((e) => toast.error(errorMessage(e, "No se pudo cargar el resumen")));
  }, [open, running]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(async () => {
      const j = (await api.get(`/reset/jobs/${job.id}`).catch(() => ({ data: job }))).data;
      setJob(j);
      if (j.status !== "en_curso") { onChanged?.(); if (j.status === "terminado") toast.success("Borrado terminado"); }
    }, 1500);
    return () => clearTimeout(t);
  }, [job, running, onChanged]);

  const inventory = async () => {
    setBusy("xlsx");
    try { saveBlob((await api.get("/reset/inventory.xlsx", { responseType: "blob", timeout: 300000 })).data, "Inventario de documentos.xlsx"); }
    catch { toast.error("No se pudo generar el inventario, intenta de nuevo"); }
    setBusy("");
  };
  const start = async () => {
    setBusy("start");
    try { setJob((await api.post("/reset/jobs", { confirm: word })).data); setOpen(true); }
    catch (e) { toast.error(errorMessage(e, "No se pudo iniciar el borrado")); }
    setBusy("");
  };
  const close = async () => {
    if (job && !running) { await api.post(`/reset/jobs/${job.id}/dismiss`).catch(() => {}); setJob(null); }
    setOpen(false);
  };
  const showJob = job && !job.dismissed;

  return (
    <>
      <section className="tr-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid="reset-card">
        <div>
          <h2 className="text-sm font-bold text-[#182235]">Borrar documentos actuales</h2>
          <p className="text-xs text-[#5b667a]">Una sola vez, antes de conectar Google Drive. Solo el Administrador.</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="tr-btn-danger px-4 py-2 text-sm" data-testid="reset-open-button">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{running ? "Borrado en curso…" : "Borrar documentos actuales"}
        </button>
      </section>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle>Borrar documentos actuales</DialogTitle>
            <DialogDescription>Los documentos se archivan (ya no se ven en ninguna pantalla) y soporte podría recuperarlos en una emergencia.</DialogDescription>
          </DialogHeader>
          {showJob && running && <Progress job={job} />}
          {showJob && !running && <Summary job={job} />}
          {!running && (!pf ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0f5ef7]" /></div> : <Preview pf={pf} />)}
          {!running && (
            <div className="space-y-2">
              <button type="button" onClick={inventory} disabled={!!busy} className="tr-btn-light px-4 py-2 text-sm" data-testid="reset-inventory-button">
                {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Descargar inventario (Excel)
              </button>
              <label className="block text-sm font-semibold text-[#182235]">Escribe {WORD} para confirmar
                <input value={word} onChange={(e) => setWord(e.target.value)} className="tr-input mt-1 w-full" data-testid="reset-confirm-input" />
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={close} className="tr-btn-light px-4 py-2 text-sm" data-testid="reset-close-button">Cerrar</button>
            {!running && <button type="button" onClick={start} disabled={word.trim() !== WORD || !!busy} className="tr-btn-danger px-4 py-2 text-sm disabled:opacity-50" data-testid="reset-confirm-button">
              {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Borrar
            </button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
